import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { classNames } from "primereact/utils";
import BusinessCustomersActions from "../../../../Redux/transition/customers/business-customer.reducer";
import { PhoneNumberInputText } from "./PhoneNumberInputText";
import { SegmentDropdown } from "./SegmentDropdown";
import { TagsMultiselect } from "./TagsMultiselect";
import { normalizePhoneNumber, isValidInternationalPhoneNumber, toLocalPhoneNumber, getInvalidMobilePrefix } from "../../../../utils/phoneUtils";
import "../../../../Styles/components/sti.css";
import "../../../../Styles/pages/inviteSubscriber.scss";
import "../../../../Styles/pages/planCreate.scss";
import "../../../../Styles/pages/invitation.scss";

const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required"),
    number: Yup.string().trim().required("Phone number is required"),
    countryCode: Yup.string(),
});

export const CreateSubscriberDialog = ({
    visible,
    onHide,
    businessId,
    defaultCountry = "GH"
}) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const toast = useRef(null);

    const creatingBusinessCustomer = useSelector((state) => state.businessCustomers.creatingBusinessCustomer);
    const createdBusinessCustomer = useSelector((state) => state.businessCustomers.createdBusinessCustomer);
    const createBusinessCustomerError = useSelector((state) => state.businessCustomers.createBusinessCustomerError);
    const fetchingSegment = useSelector((state) => state.businessCustomers.fetchingSegment);
    const fetchingTags = useSelector((state) => state.businessCustomers.fetchingTags);
    const fetchingTagGroupsGraphed = useSelector((state) => state.businessCustomers.fetchingTagGroupsGraphed);
    const tagGroupsGraphed = useSelector((state) => state.businessCustomers.tagGroupsGraphed);
    const tags = useSelector((state) => state.businessCustomers.tags);

    const [state, setState] = useState({
        success: false,
        confirmVisible: false,
        phoneToConfirm: "",
        selectedSegmentValue: null,
        selectedMultiTag: [],
    });

    const fetchSegments = useCallback((targetBusinessId) => {
        dispatch(
            BusinessCustomersActions.fetchingSegmentRequest(targetBusinessId, {
                page: 0,
                size: 1000,
                sort: "createdDate,desc",
            })
        );
    }, [dispatch]);

    const fetchTags = useCallback((targetBusinessId) => {
        dispatch(BusinessCustomersActions.tagsRequest(targetBusinessId));
    }, [dispatch]);

    const fetchTagGroupsGraphed = useCallback((targetBusinessId) => {
        dispatch(BusinessCustomersActions.tagGroupGraphedRequest(targetBusinessId));
    }, [dispatch]);

    const createBusinessCustomer = useCallback((targetBusinessId, inviteData) => {
        dispatch(BusinessCustomersActions.createBusinessCustomerRequest(targetBusinessId, inviteData));
    }, [dispatch]);

    const getEnterpriseCustomers = useCallback((targetBusinessId, options) => {
        dispatch(BusinessCustomersActions.businessCustomerAllRequest(targetBusinessId, options));
    }, [dispatch]);

    const getSummaryCustomers = useCallback((targetBusinessId, query) => {
        dispatch(BusinessCustomersActions.businessCustomerSummaryRequest(targetBusinessId, query));
    }, [dispatch]);

    const loadingMeta = fetchingSegment || fetchingTags || fetchingTagGroupsGraphed;

    const ghCountryCode = "GH";
    const phoneDefaultCountry = (defaultCountry || ghCountryCode).toLowerCase();

    const formik = useFormik({
        initialValues: {
            name: "",
            number: "",
            dialCode: "",
            countryCode: ghCountryCode,
        },
        validationSchema,
        onSubmit: (values) => {
            const normalizedPhoneNumber = normalizePhoneNumber(values.number, values.dialCode, values.countryCode);

            if (!normalizedPhoneNumber || !isValidInternationalPhoneNumber(normalizedPhoneNumber)) {
                const invalidPrefix = getInvalidMobilePrefix(values.number, values.dialCode, values.countryCode);
                formik.setFieldTouched("number", true);
                formik.setFieldError(
                    "number",
                    invalidPrefix
                        ? t("invitation.invalid_mobile_prefix", invalidPrefix)
                        : t("invitation.invalid_number")
                );
                return;
            }

            setState((prev) => ({ ...prev, phoneToConfirm: values.number, confirmVisible: true }));
        },
    });

    useEffect(() => {
        if (visible && businessId) {
            fetchSegments(businessId);
            fetchTags(businessId);
            fetchTagGroupsGraphed(businessId);
        }
    }, [visible, businessId, fetchSegments, fetchTags, fetchTagGroupsGraphed]);

    const tagsOptions = useMemo(() => {
        if (!tagGroupsGraphed?.groups) return [];

        return tagGroupsGraphed.groups
            .filter((group) => group?.tags?.length)
            .map((group) => ({
                label: group.name,
                items: (group.tags || [])
                    .filter((tag) => tag?.id && tag?.name)
                    .map((tag) => ({ label: tag.name, value: tag.id, groupId: group.id })),
            }));
    }, [tagGroupsGraphed]);

    const tagGroupById = useMemo(() => {
        return (tagGroupsGraphed?.groups || []).reduce((acc, group) => {
            (group?.tags || []).forEach((tag) => {
                if (tag?.id) {
                    acc[String(tag.id)] = String(group.id);
                }
            });
            return acc;
        }, {});
    }, [tagGroupsGraphed]);

    const tagNameById = useMemo(() => {
        const groupedTagMap = (tagGroupsGraphed?.groups || []).reduce((acc, group) => {
            (group?.tags || []).forEach((tag) => {
                if (tag?.id && tag?.name) {
                    acc[String(tag.id)] = tag.name;
                }
            });
            return acc;
        }, {});

        if (Object.keys(groupedTagMap).length > 0) {
            return groupedTagMap;
        }

        return (tags || []).reduce((acc, tag) => {
            if (tag?.id && tag?.name) {
                acc[String(tag.id)] = tag.name;
            }
            return acc;
        }, {});
    }, [tagGroupsGraphed, tags]);

    useEffect(() => {
        if (visible) {
            formik.resetForm();
            setState((prev) => ({
                ...prev,
                success: false,
                confirmVisible: false,
                phoneToConfirm: "",
                selectedSegmentValue: null,
                selectedMultiTag: [],
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const tagNamesJoinWithSlash = useMemo(() => {
        if (!Array.isArray(state.selectedMultiTag) || state.selectedMultiTag.length === 0) return null;
        return state.selectedMultiTag
            .map((tagId) => tagNameById[String(tagId)])
            .filter(Boolean)
            .join("/");
    }, [state.selectedMultiTag, tagNameById]);

    const handleTagSelectionChange = useCallback((nextSelection) => {
        const safeSelection = Array.isArray(nextSelection) ? nextSelection : [];

        setState((prev) => {
            if (safeSelection.length <= prev.selectedMultiTag.length) {
                return { ...prev, selectedMultiTag: safeSelection };
            }

            const newlyAddedTagId = safeSelection.find((tagId) => !prev.selectedMultiTag.includes(tagId));
            if (!newlyAddedTagId) {
                return { ...prev, selectedMultiTag: safeSelection };
            }

            const selectedGroupId = tagGroupById[String(newlyAddedTagId)];
            if (!selectedGroupId) {
                return { ...prev, selectedMultiTag: safeSelection };
            }

            const withoutSameGroup = prev.selectedMultiTag.filter(
                (tagId) => tagGroupById[String(tagId)] !== selectedGroupId
            );

            return { ...prev, selectedMultiTag: [...withoutSameGroup, newlyAddedTagId] };
        });
    }, [tagGroupById]);

    const showSuccess = useCallback(() => {
        toast.current?.show({
            severity: "success",
            code: "createSubscriber",
            summary: t("success"),
            detail: t("business_customers.subscriber_create_success"),
            life: 3000,
        });
    }, [t]);

    const showError = useCallback(() => {
        toast.current?.show({
            severity: "error",
            summary: t("err"),
            detail: createBusinessCustomerError?.message || t("action-error"),
            sticky: true
        });
    }, [createBusinessCustomerError, t]);

    const confirmCreateSubscriber = () => {
        if (!businessId) {
            toast.current?.show({
                severity: "error",
                summary: t("err"),
                detail: t("action-error"),
                life: 3500,
            });
            return;
        }

        const normalizedPhoneNumber = normalizePhoneNumber(
            formik.values.number,
            formik.values.dialCode,
            formik.values.countryCode || ghCountryCode
        );

        if (!normalizedPhoneNumber || !isValidInternationalPhoneNumber(normalizedPhoneNumber)) {
            const invalidPrefix = getInvalidMobilePrefix(
                formik.values.number,
                formik.values.dialCode,
                formik.values.countryCode || ghCountryCode
            );
            formik.setFieldTouched("number", true);
            formik.setFieldError(
                "number",
                invalidPrefix
                    ? t("invitation.invalid_mobile_prefix", invalidPrefix)
                    : t("invitation.invalid_number")
            );
            return;
        }

        const localPhoneNo = toLocalPhoneNumber(normalizedPhoneNumber).replace(/\D/g, "");

        const inviteData = {
            name: formik.values.name?.trim(),
            phoneNo: localPhoneNo,
            countryCode: formik.values.countryCode || "GH",
            ...(state.selectedSegmentValue ? { segment: state.selectedSegmentValue } : {}),
            ...(tagNamesJoinWithSlash ? { tags: tagNamesJoinWithSlash } : {}),
        };

        createBusinessCustomer(businessId, inviteData);
        setState((prev) => ({ ...prev, success: true, confirmVisible: false }));
    };

    const handleClose = () => {
        setState((prev) => ({ ...prev, confirmVisible: false, success: false }));
        onHide();
    };

    useEffect(() => {
        if (!creatingBusinessCustomer && state.success && createdBusinessCustomer) {
            showSuccess();
            setState((prev) => ({ ...prev, success: false }));
        }
    }, [createdBusinessCustomer, creatingBusinessCustomer, state.success, showSuccess]);

    useEffect(() => {
        if (!creatingBusinessCustomer && state.success && createBusinessCustomerError) {
            showError();
            setState((prev) => ({ ...prev, success: false }));
        }
    }, [createBusinessCustomerError, creatingBusinessCustomer, state.success, showError]);

    const isFormFieldValid = (name) => Boolean(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => (isFormFieldValid(name) ? formik.errors[name] : "");
    const hasRequiredValues = Boolean(formik.values.name?.trim()) && Boolean(formik.values.number?.trim());
    const hasValidPhone = isValidInternationalPhoneNumber(
        formik.values.number,
        formik.values.dialCode,
        formik.values.countryCode || ghCountryCode
    );
    const disableSave = creatingBusinessCustomer || loadingMeta || !hasRequiredValues || !hasValidPhone;

    const confirmationTemplate = t("business_customers.confirm_create_subscriber", {
        phone: "__PHONE__",
    });
    const [confirmationPrefix, confirmationSuffix = ""] = confirmationTemplate.split("__PHONE__");

    return (
        <>
            <Toast
                ref={toast}
                onRemove={(message) => {
                    if (message?.code === "createSubscriber" && message?.severity === "success") {
                        if (businessId) {
                            getEnterpriseCustomers(businessId, { sort: "addedOn,desc" });
                            getSummaryCustomers(businessId);
                        }
                        formik.resetForm();
                        handleClose();
                    }
                }}
            />
            <Dialog
                visible={visible}
                onHide={handleClose}
                header={t("business_customers.create_subscriber")}
                className="tier_dialog"
                id="tier_dialog"
            >
                <div style={{ padding: "1.5rem" }}>
                    <form onSubmit={formik.handleSubmit} className="p-fluid flex flex-column">
                        <div>
                            <div className="title flex">
                                <p className="labels">{t("name")}</p>
                                <p className="isterik">*</p>
                            </div>
                            <InputText
                                id="name"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={classNames({ "p-invalid": isFormFieldValid("name") })}
                                style={{ width: "100%" }}
                            />
                            {isFormFieldValid("name") && <small className="p-error">{getFormErrorMessage("name")}</small>}
                        </div>

                        <PhoneNumberInputText
                            fromSubscriberImport={false}
                            getFormErrorMessage={getFormErrorMessage}
                            formik={formik}
                            isFormFieldValid={isFormFieldValid}
                            defaultCountry={phoneDefaultCountry}
                        />

                        <SegmentDropdown
                            selectedSubscriberSegment={null}
                            selectedSegmentValue={state.selectedSegmentValue}
                            setSelectedSegmentValue={(value) => setState((prev) => ({ ...prev, selectedSegmentValue: value }))}
                        />

                        <TagsMultiselect
                            selectedMultiTag={state.selectedMultiTag}
                            setSelectedMultiTag={handleTagSelectionChange}
                            options={tagsOptions}
                            panelClassName="create-subscriber-tag-panel"
                        />

                        <Button
                            type="submit"
                            loading={creatingBusinessCustomer}
                            disabled={disableSave}
                            className="p-mt-2 mt-5"
                            id="send_invite_btn">
                            {t("common.save")}
                        </Button>
                    </form>
                </div>
            </Dialog>

            <ConfirmDialog
                visible={state.confirmVisible}
                onHide={() => setState((prev) => ({ ...prev, confirmVisible: false }))}
                className="confirm_close_btn"
                id="tier_dialog"
                message={(
                    <span>
                        {confirmationPrefix}
                        <span className="create-subscriber-confirm-phone">{state.phoneToConfirm}</span>
                        {confirmationSuffix}
                    </span>
                )}
                header={t("business_customers.confirmation")}
                accept={confirmCreateSubscriber}
                reject={() => setState((prev) => ({ ...prev, confirmVisible: false }))}
                acceptLabel={t("save")}
                rejectLabel={t("cancel")}
            />
        </>
    );
};
